import { useCallback } from 'react';
import { useSchool } from '../contexts/SchoolContext';
// This school-scoped adapter is the boundary that owns the underlying service.
// eslint-disable-next-line no-restricted-imports
import { firestoreService } from '../firestoreService';

/**
 * School-scoped collections that require schoolId
 */
const SCHOOL_SCOPED_COLLECTIONS = [
  'students',
  'classes',
  'mentions',
  'offences',
  'values',
  'disciplineRules',
  'queue',
  'disciplineQueue',
  'seatingPlans',
  'smartLists',
  'consequenceTypes',
  'disciplineCategories',
  'thresholdRules',
  'escalationRules',
  'mentionRules',
  'mentionTypes',
  'flagTypes',
  'zeroToleranceRules',
  'accountabilityRules',
  'escalationTracking',
  'ruleApplications',
  'accountabilityPeriods',
  'behaviorContracts',
  'studentContracts',
  'dailyStats',
  'menuItems',
  'messageTypeSettings',
  'schoolPeriods',
  'messages',
  'unitMemberships',
  'organizationalUnits',
  'roleTemplates',
  'subjects',
  'timetableAssignments',
  'staffProfiles',
  'calendarEvents',
  'familyCards',
  'broadcastLogs',
  'roles',
  'teams',
  'hierarchies',
  'activityTypes',
  'activities',
  'sessionReports',
  'todos',
  'tasks',
  'workflowTemplates',
  'workflowInstances',
  'grades',
  'domains',
  'recognitions',
  'accessPasses',
  'accessLogs',
  'agentMemories',
  'communications',
  'gmailConnections',
  'participationHats',
  'participationEvents',
  'academicsEvents',
  'academicsRules',
  'academicsActions',
  'academicsTypes',
  'academicsQueue',
  'academics_ruleFires',
  'messageTemplates',
];

const DYNAMIC_DOMAIN_COLLECTION =
  /^domain_[a-z0-9_]+_(events|types|categories|actions|rules|queue|ruleFires)$/;

export function isSchoolScopedCollection(collectionName) {
  return SCHOOL_SCOPED_COLLECTIONS.includes(collectionName)
    || DYNAMIC_DOMAIN_COLLECTION.test(String(collectionName || ''));
}

/**
 * Hook that wraps firestoreService with automatic schoolId injection
 *
 * Usage:
 * const { create, update, remove, query, getAll } = useSchoolFirestore();
 *
 * // schoolId is automatically added to creates for school-scoped collections
 * await create('students', { firstName: 'John', surname: 'Doe' });
 *
 * @returns {Object} Firestore CRUD operations with schoolId auto-injection
 */
export const useSchoolFirestore = () => {
  const { schoolId, hasSchool } = useSchool();

  /**
   * Create a document with automatic schoolId injection for school-scoped collections
   */
  const create = useCallback(async (collectionName, data) => {
    // For school-scoped collections, use hierarchical path
    if (isSchoolScopedCollection(collectionName)) {
      if (!schoolId) {
        console.warn(`[useSchoolFirestore] Creating ${collectionName} document without schoolId - user may not have school assigned yet`);
        return null;
      }

      // Use hierarchical path: schools/{schoolId}/{collection}
      const hierarchicalPath = `schools/${schoolId}/${collectionName}`;
      return firestoreService.create(hierarchicalPath, data);
    }

    // For non-school-scoped collections, create normally
    return firestoreService.create(collectionName, data);
  }, [schoolId]);

  /**
   * Update a document (uses hierarchical path for school-scoped collections)
   */
  const update = useCallback(async (collectionName, id, data) => {
    if (isSchoolScopedCollection(collectionName)) {
      if (!schoolId) {
        console.warn(`[useSchoolFirestore] Updating ${collectionName}/${id} without schoolId - blocked to prevent writing to wrong path`);
        return null;
      }
      const hierarchicalPath = `schools/${schoolId}/${collectionName}`;
      return firestoreService.update(hierarchicalPath, id, data);
    }
    return firestoreService.update(collectionName, id, data);
  }, [schoolId]);

  /**
   * Delete a document (uses hierarchical path for school-scoped collections)
   */
  const remove = useCallback(async (collectionName, id) => {
    if (isSchoolScopedCollection(collectionName)) {
      if (!schoolId) {
        console.warn(`[useSchoolFirestore] Deleting ${collectionName}/${id} without schoolId - blocked to prevent deleting from wrong path`);
        return null;
      }
      const hierarchicalPath = `schools/${schoolId}/${collectionName}`;
      return firestoreService.delete(hierarchicalPath, id);
    }
    return firestoreService.delete(collectionName, id);
  }, [schoolId]);

  /**
   * Query documents (uses hierarchical path for school-scoped collections)
   */
  const query = useCallback(async (collectionName, conditions = []) => {
    if (isSchoolScopedCollection(collectionName)) {
      if (!schoolId) {
        console.warn(`[useSchoolFirestore] Querying ${collectionName} without schoolId - returning empty`);
        return [];
      }
      const hierarchicalPath = `schools/${schoolId}/${collectionName}`;
      return firestoreService.query(hierarchicalPath, conditions);
    }
    return firestoreService.query(collectionName, conditions);
  }, [schoolId]);

  /**
   * Get all documents using hierarchical paths for school-scoped collections
   */
  const getAll = useCallback(async (collectionName) => {
    if (isSchoolScopedCollection(collectionName)) {
      if (!schoolId) {
        console.warn(`[useSchoolFirestore] Getting all ${collectionName} without schoolId - returning empty`);
        return [];
      }
      const hierarchicalPath = `schools/${schoolId}/${collectionName}`;
      return firestoreService.getAll(hierarchicalPath);
    }
    return firestoreService.getAll(collectionName);
  }, [schoolId]);

  /**
   * Get a single document by ID using hierarchical paths for school-scoped collections
   */
  const getById = useCallback(async (collectionName, id) => {
    if (isSchoolScopedCollection(collectionName)) {
      if (!schoolId) {
        console.warn(`[useSchoolFirestore] Getting ${collectionName}/${id} without schoolId - returning null`);
        return null;
      }
      const hierarchicalPath = `schools/${schoolId}/${collectionName}`;
      return firestoreService.get(hierarchicalPath, id);
    }
    return firestoreService.get(collectionName, id);
  }, [schoolId]);

  /** Explicit school collection subscription, including school-created Domains. */
  const subscribeSchoolCollection = useCallback((collectionName, callback, conditions = [], onError) => {
    if (!schoolId || !collectionName || collectionName.includes('/')) {
      const error = new Error('A current school and a collection name are required.');
      if (onError) onError(error);
      return () => {};
    }
    return firestoreService.subscribe(`schools/${schoolId}/${collectionName}`, callback, conditions, onError);
  }, [schoolId]);

  return {
    create,
    update,
    remove,
    query,
    getAll,
    getById,
    subscribeSchoolCollection,
    // Expose schoolId and helper flags for components that need them
    schoolId,
    hasSchool,
    // Helper to check if a collection is school-scoped
    isSchoolScoped: useCallback((collectionName) => {
      return isSchoolScopedCollection(collectionName);
    }, [])
  };
};

export default useSchoolFirestore;
